import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import RouteTransition from "@/components/RouteTransition";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import PaidRoute from "@/components/PaidRoute";
import AdminRoute from "@/components/AdminRoute";
import { InstitutionRoute, TeacherRoute, ParentRoute, OrgMemberRoute } from "@/components/RoleRoute";
import SchoolsShell from "@/components/schools/SchoolsShell";
import PrincipalDashboard from "./pages/schools/PrincipalDashboard";
import SchoolsStudentProfile from "./pages/schools/StudentProfile";
import SchoolsParentDashboard from "./pages/schools/ParentDashboard";
import CounsellorDashboard from "./pages/schools/CounsellorDashboard";
import StudiosDashboard from "./pages/schools/StudiosDashboard";
import PortfolioDashboard from "./pages/schools/PortfolioDashboard";
import SchoolsGlossary from "./pages/schools/Glossary";
import SchoolsBoardpack from "./pages/schools/Boardpack";
import SchoolsDemoScript from "./pages/schools/DemoScript";
import SchoolsOnePager from "./pages/schools/OnePager";
import ComingSoon from "@/components/schools/ComingSoon";
import Institution from "./pages/Institution";
import InstitutionAudit from "./pages/InstitutionAudit";
import InstitutionIntelligence from "./pages/InstitutionIntelligence";
import Teacher from "./pages/Teacher";
import ParentDashboard from "./pages/Parent";
import ParentAccept from "./pages/ParentAccept";
import AdminFlags from "./pages/AdminFlags";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import AuthError from "./pages/AuthError";
import AuthDebug from "./pages/AuthDebug";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";

import Start from "./pages/Start";
import PassportConsent from "./pages/PassportConsent";
import PassportGuardian from "./pages/PassportGuardian";
import ConsentGate from "./components/ConsentGate";
import SecondOpinion from "./pages/SecondOpinion";
import ObserverInput from "./pages/ObserverInput";
import HomeV2 from "./pages/HomeV2";
import JourneyHub from "./pages/hubs/JourneyHub";
import DnaHub from "./pages/hubs/DnaHub";
import YouHub from "./pages/hubs/YouHub";
import Onboarding from "./pages/Onboarding";

import AssessmentV2 from "./pages/AssessmentV2";
import RouteErrorBoundary from "./components/RouteErrorBoundary";
import AssessmentV2Result from "./pages/AssessmentV2Result";
import AssessmentV2Dossier from "./pages/AssessmentV2Dossier";
import MirrorMoment from "./pages/MirrorMoment";
import FutureCommandCenter from "./pages/FutureCommandCenter";
import Report from "./pages/Report";
import Coach from "./pages/Coach";
import Interview from "./pages/Interview";
import Resume from "./pages/Resume";
import GrowthPlan from "./pages/GrowthPlan";
import NextSteps from "./pages/NextSteps";
import VoiceJournal from "./pages/VoiceJournal";
import ParentReport from "./pages/ParentReport";
import Goals from "./pages/Goals";
import Challenges from "./pages/Challenges";
import Opportunities from "./pages/Opportunities";
import CareersExplorer from "./pages/CareersExplorer";
import CareerDetail from "./pages/CareerDetail";
import Certifications from "./pages/Certifications";
import CertificationDetail from "./pages/CertificationDetail";
import SharedReport from "./pages/SharedReport";
import SharedDossier from "./pages/SharedDossier";
import Offer from "./pages/Offer";
import Docs from "./pages/Docs";
import Privacy from "./pages/Privacy";
import PrivacyConsent from "./pages/PrivacyConsent";
import Terms from "./pages/Terms";
import PolicyPage from "./pages/PolicyPage";
import CookieConsent from "./components/CookieConsent";
import GA4RouteTracker from "./components/GA4RouteTracker";
import Payment from "./pages/Payment";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentFailure from "./pages/PaymentFailure";
import AdminHub from "./pages/AdminHub";
import AdminPricingAnalytics from "./pages/AdminPricingAnalytics";

import AdminPayments from "./pages/AdminPayments";
import AdminPaymentConsole from "./pages/AdminPaymentConsole";
import AdminWebhookLog from "./pages/AdminWebhookLog";
import AdminEmailLog from "./pages/AdminEmailLog";
import AdminAnalytics from "./pages/AdminAnalytics";
import AdminObservability from "./pages/AdminObservability";
import AdminCoupons from "./pages/AdminCoupons";
import AdminAccessBenefits from "./pages/AdminAccessBenefits";
import AdminScholarships from "./pages/AdminScholarships";
import ScholarshipApply from "./pages/ScholarshipApply";
import AdminBenefitsConsole from "./pages/AdminBenefitsConsole";
import AdminCouponHistory from "./pages/AdminCouponHistory";
import AdminCompInvites from "./pages/AdminCompInvites";
import AdminBackend from "./pages/AdminBackend";
import AdminWaitlists from "./pages/AdminWaitlists";
import Redeem from "./pages/Redeem";
import Meeting from "./pages/Meeting";
import AdminDemoBranding from "./pages/AdminDemoBranding";
import AdminEvidenceIntelligence from "./pages/AdminEvidenceIntelligence";
import AdminNotificationsHealth from "./pages/AdminNotificationsHealth";
import InstitutionBriefPrint from "./pages/InstitutionBriefPrint";
import PrincipalDemoAccess from "./pages/PrincipalDemoAccess";
import StudentQuickstart from "./pages/StudentQuickstart";
import AdminLaunchOffer from "./pages/AdminLaunchOffer";
import InstitutionThankYou from "./pages/InstitutionThankYou";
import ParentGuide from "./pages/ParentGuide";
import FreeHub from "./pages/free/FreeHub";
import CareerQuiz from "./pages/free/CareerQuiz";
import AdminParentGuide from "./pages/AdminParentGuide";
import AdminReports from "./pages/AdminReports";
import AdminColleges from "./pages/AdminColleges";
import CollegeMatch from "./pages/CollegeMatch";
import AdminAuditLog from "./pages/AdminAuditLog";


import NotFound from "./pages/NotFound";
import Faqs from "./pages/Faqs";
import OccupationStandards from "./pages/OccupationStandards";
import Refer from "./pages/Refer";
import RefRedirect from "./pages/RefRedirect";
import Leaderboard from "./pages/Leaderboard";
import Rewards from "./pages/Rewards";
import Demo from "./pages/Demo";
import PitchDeck from "./pages/PitchDeck";
import DemoBar from "./components/demo/DemoBar";
import CareerCardPage from "./pages/CareerCardPage";
import ClarityCardPage from "./pages/ClarityCardPage";
import Affiliate from "./pages/Affiliate";
import AffiliateRedirect from "./pages/AffiliateRedirect";
import Journey from "./pages/Journey";
import Wrapped from "./pages/Wrapped";
import Reflect from "./pages/Reflect";
import Settings from "./pages/Settings";
import Portfolio from "./pages/Portfolio";
import Wap3Portfolio from "./pages/Wap3Portfolio";
import VisionBoard from "./pages/VisionBoard";
import SkillsIntelligence from "./pages/SkillsIntelligence";
import Profile from "./pages/Profile";
import LinkedInCallback from "./pages/LinkedInCallback";
import ProWaitlist from "./pages/ProWaitlist";
import Unsubscribe from "./pages/Unsubscribe";
import OAuthConsent from "./pages/OAuthConsent";
import FirecrawlCheck from "./pages/FirecrawlCheck";
import FirebaseAuthDemo from "./pages/FirebaseAuthDemo";

import DashboardFab from "@/components/DashboardFab";
import ScrollToTop from "@/components/ScrollToTop";
import CanonicalRedirect from "@/components/CanonicalRedirect";
import AutoReveal from "@/components/anim/AutoReveal";
import ReferralBanner from "@/components/refer/ReferralBanner";
import AnswerDrain from "@/components/AnswerDrain";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <AnswerDrain />
          <ScrollToTop />
          <CanonicalRedirect />
          <AutoReveal />
          <DashboardFab />
          <ReferralBanner />
          <DemoBar />
          <CookieConsent />
          <GA4RouteTracker />
          <RouteTransition>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/demo" element={<Demo />} />
            <Route path="/pitch-deck" element={<PitchDeck />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/auth/error" element={<AuthError />} />
            <Route path="/auth/debug" element={<AdminRoute><AuthDebug /></AdminRoute>} />

            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/auth/linkedin/callback" element={<LinkedInCallback />} />
            <Route path="/offer" element={<Offer />} />
            <Route path="/docs" element={<Docs />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/privacy-consent" element={<PrivacyConsent />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/cookies" element={<PolicyPage doc="cookies" />} />
            <Route path="/child-safety" element={<PolicyPage doc="child-safety" />} />
            <Route path="/responsible-ai" element={<PolicyPage doc="responsible-ai" />} />
            <Route path="/mentor-code" element={<PolicyPage doc="mentor-code" />} />
            <Route path="/p/:handle" element={<Portfolio />} />
            <Route path="/portfolio" element={<ProtectedRoute><Wap3Portfolio /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/reflect/:monthIndex" element={<ProtectedRoute><Reflect /></ProtectedRoute>} />
            <Route path="/faqs" element={<Faqs />} />
            <Route path="/occupation-standards" element={<OccupationStandards />} />
            <Route path="/parent-guide" element={<ParentGuide />} />
            <Route path="/free" element={<FreeHub />} />
            <Route path="/free/career-quiz" element={<CareerQuiz />} />
            <Route path="/pro-waitlist" element={<ProWaitlist />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
            <Route path="/dev/firecrawl-check" element={<AdminRoute><FirecrawlCheck /></AdminRoute>} />
            <Route path="/firebase-auth" element={<AdminRoute><FirebaseAuthDemo /></AdminRoute>} />
            <Route path="/ref/:handle" element={<RefRedirect />} />
            <Route path="/a/:code" element={<AffiliateRedirect />} />
            <Route path="/affiliate" element={<ProtectedRoute><Affiliate /></ProtectedRoute>} />
            <Route path="/refer" element={<ProtectedRoute><Refer /></ProtectedRoute>} />
            <Route path="/leaderboard" element={<ProtectedRoute><Leaderboard /></ProtectedRoute>} />
            <Route path="/rewards" element={<ProtectedRoute><Rewards /></ProtectedRoute>} />
            <Route path="/card" element={<ProtectedRoute><CareerCardPage /></ProtectedRoute>} />
            {/* One page of the Passport, safe to post: no scores, no pay. */}
            <Route path="/clarity-card" element={<ProtectedRoute><ClarityCardPage /></ProtectedRoute>} />
            <Route path="/payment" element={<Payment />} />
            <Route path="/pricing" element={<Navigate to="/payment" replace />} />
            <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />

            <Route path="/payment-success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
            <Route path="/payment-failure" element={<PaymentFailure />} />
            <Route path="/home" element={<PaidRoute><HomeV2 /></PaidRoute>} />
            <Route path="/hub/journey" element={<ProtectedRoute><JourneyHub /></ProtectedRoute>} />
            <Route path="/hub/dna" element={<ProtectedRoute><DnaHub /></ProtectedRoute>} />
            <Route path="/hub/you" element={<ProtectedRoute><YouHub /></ProtectedRoute>} />
            <Route path="/dashboard" element={<PaidRoute><Dashboard /></PaidRoute>} />
            <Route path="/start" element={<ProtectedRoute><ConsentGate><Start /></ConsentGate></ProtectedRoute>} />
            {/* DPDP Act. For a learner under 18, payment success must not lead
                straight into the walk: adult verification and explicit
                guardian consent come first. The database enforces this; these
                two routes are how a guardian satisfies it and later manages it. */}
            <Route path="/passport/consent" element={<ProtectedRoute><PassportConsent /></ProtectedRoute>} />
            <Route path="/passport/guardian" element={<ProtectedRoute><PassportGuardian /></ProtectedRoute>} />
            {/* X-OBSERVER-001. The learner side needs an account; the observer
                side must never need one — a parent with a link is not a user. */}
            <Route path="/second-opinion" element={<ProtectedRoute><SecondOpinion /></ProtectedRoute>} />
            <Route path="/observe/:token" element={<ObserverInput />} />
            {/* One walk only. The legacy v1 runtime is retired — every
                "take the assessment" link lands on the band picker, which
                routes into the canonical v2 walk. */}
            <Route path="/assessment" element={<Navigate to="/start" replace />} />
            <Route path="/assessment/v2" element={<ProtectedRoute><ConsentGate><AssessmentV2 /></ConsentGate></ProtectedRoute>} />
            <Route
              path="/assessment/v2/result"
              element={
                <ProtectedRoute>
                  {/* The result surface reads the whole run and the sealed
                      report; a crash here must never read as "my answers
                      vanished". */}
                  <RouteErrorBoundary name="AssessmentV2Result" fallbackHref="/dashboard">
                    <AssessmentV2Result />
                  </RouteErrorBoundary>
                </ProtectedRoute>
              }
            />
            <Route path="/assessment/v2/dossier" element={<ProtectedRoute><AssessmentV2Dossier /></ProtectedRoute>} />

            <Route path="/mirror" element={<ProtectedRoute><MirrorMoment /></ProtectedRoute>} />
            <Route path="/command-center" element={<PaidRoute><FutureCommandCenter /></PaidRoute>} />
            <Route path="/report" element={<PaidRoute><Report /></PaidRoute>} />
            <Route path="/coach" element={<PaidRoute><Coach /></PaidRoute>} />
            <Route path="/interview" element={<PaidRoute><Interview /></PaidRoute>} />
            <Route path="/resume" element={<PaidRoute><Resume /></PaidRoute>} />
            <Route path="/growth-plan" element={<PaidRoute><GrowthPlan /></PaidRoute>} />
            {/* Next Step AI. Signed in is enough: the first action of each
                month is free, the rest is held server-side. */}
            <Route path="/next-steps" element={<ProtectedRoute><NextSteps /></ProtectedRoute>} />
            <Route path="/journey" element={<ProtectedRoute><Journey /></ProtectedRoute>} />
            <Route path="/vision-board" element={<PaidRoute><VisionBoard /></PaidRoute>} />
            <Route path="/skills" element={<PaidRoute><SkillsIntelligence /></PaidRoute>} />
            <Route path="/wrapped" element={<ProtectedRoute><Wrapped /></ProtectedRoute>} />
            <Route path="/journal" element={<PaidRoute><VoiceJournal /></PaidRoute>} />
            <Route path="/parent-report" element={<ProtectedRoute><ParentReport /></ProtectedRoute>} />
            <Route path="/goals" element={<PaidRoute><Goals /></PaidRoute>} />
            <Route path="/challenges" element={<PaidRoute><Challenges /></PaidRoute>} />
            <Route path="/opportunities" element={<PaidRoute><Opportunities /></PaidRoute>} />

            <Route path="/careers" element={<ProtectedRoute><CareersExplorer /></ProtectedRoute>} />
            <Route path="/careers/:id" element={<ProtectedRoute><CareerDetail /></ProtectedRoute>} />
            <Route path="/certifications" element={<ProtectedRoute><Certifications /></ProtectedRoute>} />
            <Route path="/certifications/:id" element={<ProtectedRoute><CertificationDetail /></ProtectedRoute>} />
            <Route path="/parent/:token" element={<ParentReport />} />
            <Route path="/r/:token" element={<SharedReport />} />
            <Route path="/d/:token" element={<SharedDossier />} />
            <Route path="/admin" element={<AdminRoute><AdminHub /></AdminRoute>} />
            <Route path="/admin/audit-log" element={<AdminRoute><AdminAuditLog /></AdminRoute>} />
            
            <Route path="/admin/payments" element={<AdminRoute><AdminPayments /></AdminRoute>} />

            <Route path="/admin/payment-console" element={<AdminRoute><AdminPaymentConsole /></AdminRoute>} />
            <Route path="/admin/webhook-log" element={<AdminRoute><AdminWebhookLog /></AdminRoute>} />
            <Route path="/admin/email-log" element={<AdminRoute><AdminEmailLog /></AdminRoute>} />
            <Route path="/admin/analytics" element={<AdminRoute><AdminAnalytics /></AdminRoute>} />
            <Route path="/admin/pricing-analytics" element={<AdminRoute><AdminPricingAnalytics /></AdminRoute>} />
            <Route path="/admin/observability" element={<AdminRoute><AdminObservability /></AdminRoute>} />
            <Route path="/admin/coupons" element={<AdminRoute><AdminCoupons /></AdminRoute>} />
            <Route path="/admin/benefits" element={<AdminRoute><AdminBenefitsConsole /></AdminRoute>} />
            <Route path="/admin/access-benefits" element={<AdminRoute><AdminAccessBenefits /></AdminRoute>} />
            <Route path="/admin/scholarships" element={<AdminRoute><AdminScholarships /></AdminRoute>} />
            <Route path="/scholarship" element={<ScholarshipApply />} />
            <Route path="/admin/launch-offer" element={<AdminRoute><AdminLaunchOffer /></AdminRoute>} />
            <Route path="/admin/coupons/history" element={<AdminRoute><AdminCouponHistory /></AdminRoute>} />
            <Route path="/admin/invites" element={<AdminRoute><AdminCompInvites /></AdminRoute>} />
            <Route path="/admin/waitlists" element={<AdminRoute><AdminWaitlists /></AdminRoute>} />
            <Route path="/admin/flags" element={<AdminRoute><AdminFlags /></AdminRoute>} />
            <Route path="/admin/backend" element={<AdminRoute><AdminBackend /></AdminRoute>} />
            <Route path="/institution" element={<InstitutionRoute><Institution /></InstitutionRoute>} />
            <Route path="/institution/audit" element={<InstitutionRoute><InstitutionAudit /></InstitutionRoute>} />
            <Route path="/institution/intelligence" element={<InstitutionRoute><InstitutionIntelligence /></InstitutionRoute>} />
            <Route path="/institution/brief/print" element={<InstitutionRoute><InstitutionBriefPrint /></InstitutionRoute>} />
            <Route path="/teacher" element={<TeacherRoute><Teacher /></TeacherRoute>} />
            <Route path="/parent" element={<ParentRoute><ParentDashboard /></ParentRoute>} />
            <Route path="/parent/accept" element={<ParentAccept />} />
            <Route path="/redeem" element={<Redeem />} />
            <Route path="/meeting" element={<Meeting />} />
            <Route path="/principal-demo-access" element={<PrincipalDemoAccess />} />
            <Route path="/student-quickstart" element={<StudentQuickstart />} />
            <Route path="/institution-thank-you" element={<InstitutionThankYou />} />
            <Route path="/admin/demo-institution" element={<AdminRoute><AdminDemoBranding /></AdminRoute>} />
            <Route path="/admin/evidence-governance" element={<AdminRoute><AdminEvidenceIntelligence /></AdminRoute>} />
            <Route path="/admin/evidence-intelligence" element={<Navigate to="/admin/evidence-governance" replace />} />
            <Route path="/admin/career-enrichment" element={<Navigate to="/admin/evidence-governance" replace />} />

            <Route path="/admin/notifications/health" element={<AdminRoute><AdminNotificationsHealth /></AdminRoute>} />
            <Route path="/admin/parent-guide" element={<AdminRoute><AdminParentGuide /></AdminRoute>} />
            <Route path="/admin/reports" element={<AdminRoute><AdminReports /></AdminRoute>} />
            <Route path="/admin/colleges" element={<AdminRoute><AdminColleges /></AdminRoute>} />
            <Route path="/colleges" element={<ProtectedRoute><CollegeMatch /></ProtectedRoute>} />

            {/* WeKIT Education Intelligence™ — the /schools suite. Nested under
                the shell so the sidebar, command palette and presenter mode are
                shared. Print surfaces (one-pagers, board pack) sit outside the
                shell so they print clean. */}
            <Route path="/schools" element={<OrgMemberRoute><SchoolsShell /></OrgMemberRoute>}>
              <Route index element={<PrincipalDashboard />} />
              <Route path="student" element={<SchoolsStudentProfile />} />
              <Route
                path="students"
                element={
                  <ComingSoon
                    title="Student roster"
                    description="Every learner in your school, with clarity and readiness at a glance."
                    highlights={[
                      "Searchable roster by grade and house",
                      "Completion and clarity per learner",
                      "Jump straight into a student profile",
                      "Bulk invites and seat management",
                    ]}
                  />
                }
              />

              <Route path="parent" element={<SchoolsParentDashboard />} />
              <Route path="counsellor" element={<CounsellorDashboard />} />
              <Route path="studios" element={<StudiosDashboard />} />
              <Route path="portfolio" element={<PortfolioDashboard />} />
              <Route path="glossary" element={<SchoolsGlossary />} />
              <Route
                path="reports"
                element={
                  <ComingSoon
                    title="Cohort reports"
                    description="Downloadable termly reports for governors, boards and district partners."
                    highlights={[
                      "Termly cohort progress packs",
                      "Grade and house comparisons",
                      "Export to PDF and CSV",
                      "Shareable board summary",
                    ]}
                  />
                }
              />
              <Route
                path="settings"
                element={
                  <ComingSoon
                    title="School settings"
                    description="Branding, cohorts, staff roles and data retention for your tenant."
                    highlights={[
                      "School branding and logo",
                      "Cohort and house setup",
                      "Staff roles and invitations",
                      "Data retention controls",
                    ]}
                  />
                }
              />
            </Route>
            <Route path="/schools/script" element={<OrgMemberRoute><SchoolsDemoScript /></OrgMemberRoute>} />
            <Route path="/schools/boardpack" element={<OrgMemberRoute><SchoolsBoardpack /></OrgMemberRoute>} />
            <Route path="/schools/:audience/onepager" element={<OrgMemberRoute><SchoolsOnePager /></OrgMemberRoute>} />
            {/* "Live tenant" is the real institution surface, not a demo route. */}
            <Route path="/schools/live" element={<Navigate to="/institution" replace />} />

            <Route path="*" element={<NotFound />} />

          </Routes>
          </RouteTransition>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
